import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { css } from '@emotion/react';
import * as React from 'react';
import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';

const traceStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly field: DocumentFieldView;
};

/** "Vérifiée par … le …": who reviewed the field and when, the reviewer being resolved against the agency directory. Renders nothing for a field nobody reviewed. */
const FieldReviewTrace: React.FC<Props> = ({ field }) => {
	const { data: facilityUsers } = useFacilityUsers();

	const trace = React.useMemo((): string | null => {
		const status = field.verification.value;

		if (field.reviewedAt === null || (status !== 'verified' && status !== 'corrected')) {
			return null;
		}

		const verb = status === 'corrected' ? 'Corrigée' : 'Vérifiée';
		const reviewer = facilityUsers?.findById(field.reviewedById) ?? null;
		const datePart = formatLongDate(field.reviewedAt);

		return reviewer === null
			? `${verb} le ${datePart}`
			: `${verb} par ${reviewer.fullName} le ${datePart}`;
	}, [field, facilityUsers]);

	if (trace === null) {
		return null;
	}

	return <p css={traceStyle}>{trace}</p>;
};

export default FieldReviewTrace;
